import {
	createStyles,
	LoadingOverlay,
	Stack,
	useMantineTheme,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useSuspenseApiMemberEnv } from '@repo/api-codegen';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { titleCase } from '@repo/common/utils';
import {
	integrationsQueryKeyFactory,
	useCreateIntegration,
	useIntegrationList,
	useSuspenseFeatureAccess,
} from '../../api';
import { useMarketplaceIntegrationSpecList } from '../../api/hooks/marketplace';
import { shouldShowIntegrationWithFeatureFlag } from '../../components/Integration/AddIntegrationModal/AddIntegrationModal.helpers';
import { IntegrationSpecCard } from '../../components/IntegrationSpecCard/IntegrationSpecCard';
import WidgetSelector from '../../components/WidgetSelector';
import type { WidgetData } from '../../components/WidgetSelector/types';
import { usePlan } from '../../hooks/usePlans';
import type { IntegrationSpec } from '../../interfaces/IntegrationSpec';
import { IS_LOCAL_OR_DEVELOPMENT } from '../../utils/envs';
import { useFeatureFlags } from '../../utils/featureFlags';

const useStyles = createStyles((theme) => ({
	selectorViewWrapper: {
		paddingBottom: 0,
	},
	widgetItemsWrapper: {
		columnGap: theme.spacing.lg,
		rowGap: theme.spacing.md,
	},
	widgetWrapper: {
		paddingBottom: 0,
	},
}));

export function BrowseTab() {
	const theme = useMantineTheme();

	const { classes } = useStyles();
	const { isPayingCustomer, trial } = usePlan();

	const { data: memberEnv } = useSuspenseApiMemberEnv({});

	const navigate = useNavigate();
	const { isPending: isLoading, mutateAsync: apiCreateIntegration } =
		useCreateIntegration({
			invalidationKeys: [integrationsQueryKeyFactory.allLists()],
		});
	const { data: marketplaceIntegrationSpecs } =
		useMarketplaceIntegrationSpecList({
			filters: {
				browsing: true,
			},
			options: {
				select: (res) => res.results,
			},
		});

	const featureFlags = useFeatureFlags();
	const featureAccess = useSuspenseFeatureAccess();
	const { data: integrations } = useIntegrationList({});

	const builtinIntegrationSpecsData = integrationList
		.filter(
			(integration) =>
				!integration.hidden &&
				integration.secodaEnabled &&
				(memberEnv?.SLACK_ENABLED || integration.type !== 'slack')
		)
		.filter((integration) =>
			shouldShowIntegrationWithFeatureFlag({
				integration,
				featureFlags,
				existingIntegrations: integrations,
			})
		)
		.filter((integration) => {
			// If the integration has secodaCoreEnabled set to true, we need to check if the user has access to it with secodaCoreAccess feature access
			if (integration.secodaCoreEnabled === true) {
				return featureAccess.secodaCoreAccess;
			}
			// If the integration has secodaCoreEnabled set to false or undefined, we allow it by default
			return true;
		})
		.map((integration) => ({
			group: integration.group,
			title: integration.name,
			data: { type: 'builtin', value: integration } as IntegrationSpec,
		}));

	const marketplaceIntegrationSpecsData = (marketplaceIntegrationSpecs || [])
		.filter((spec) => spec.versions.length > 0)
		.map((spec) => ({
			group: titleCase(spec.versions[0].category.toLowerCase()).replace(
				'_',
				' '
			),
			title: spec.versions[0].name,
			data: { type: 'marketplace', value: spec } as IntegrationSpec,
			key: spec.id,
		}));

	const data = [
		...builtinIntegrationSpecsData,
		...marketplaceIntegrationSpecsData,
	];

	const createCustomIntegration = async () => {
		// In production, trial users and non-paying customers can't create custom
		// integrations.
		if (!IS_LOCAL_OR_DEVELOPMENT && (!isPayingCustomer || trial)) {
			showNotification({
				title: 'Custom integrations are not available in your plan.',
				message:
					'Please upgrade to a paying plan to create custom integrations.',
				color: 'red',
			});
			return;
		}

		const integration = await apiCreateIntegration({
			data: {
				name: 'Untitled',
				type: 'custom',
				credentials: {},
			},
		});
		navigate(`/integrations/${integration.id}/`);
	};

	const handleIntegrationSelect = async (
		widgetData: WidgetData<IntegrationSpec>
	) => {
		if (widgetData.data.type === 'builtin') {
			if (widgetData.data.value.type === 'custom') {
				await createCustomIntegration();
				return;
			}
			navigate(`/integrations/new?type=${widgetData.data.value.type}`);
		} else {
			navigate(
				`/integrations/new?selectedMarketplaceSpec=${widgetData.data.value.id}`
			);
		}
	};

	return (
		<Stack spacing={0}>
			<LoadingOverlay
				visible={isLoading}
				overlayBlur={2}
				loaderProps={{ color: theme.other.neutralColors.black }}
			/>

			<WidgetSelector<IntegrationSpec>
				classNames={{ selectorViewWrapper: classes.selectorViewWrapper }}
				data={data}
				onWidgetSelect={handleIntegrationSelect}
				widgetGroupProps={{
					classNames: {
						widgetItemsWrapper: classes.widgetItemsWrapper,
					},
				}}
				scrollAreaHeight={`calc(100vh - ${theme.other.calculateSpace(54)})`}
				renderer={IntegrationSpecCard}
				cols={3}
			/>
		</Stack>
	);
}
